import { trpc, getAuthToken } from "@/lib/trpc";
import { auth } from "@/lib/firebase";
import { signOut } from "firebase/auth";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import "./index.css";
import { registerSW } from "virtual:pwa-register";
import { isVideoLivePath } from "@/lib/videoRoomPath";
import { isPublicLinkPage } from "@shared/publicLinks";
import { getRoomSessionToken } from "@/lib/videoInterviewSession";
import { VIDEO_INTERVIEW_SESSION_HEADER } from "@shared/videoInterview";
import { rememberInAppNavigation } from "@/lib/inAppNavigation";

// Проверяем обновление при возврате в CRM и на старте: рекрутёры держат
// вкладки открытыми днями.
//
// Вкладка, открытая на маршруте комнаты, service worker не регистрирует
// вовсе. Мало не предлагать в ней обновление: сама регистрация в режиме
// «prompt» вешает на ожидающую сборку слушателя `controlling`, и когда
// человек в СОСЕДНЕЙ вкладке нажимает «Обновить», новая сборка забирает все
// вкладки разом — живая встреча перезагружается (инцидент 19.09.2026, вторая
// дверь к нему найдена на разборе 20.09). Заодно телефон кандидата не качает
// в кэш всю CRM (около 6 МБ) в первые секунды встречи, когда канал нужен
// видео. Обновление дождётся выхода из комнаты.
//
// Список встреч (/video-interviews) сюда НЕ входит: это обычный рабочий экран,
// рекрутёр держит его открытым весь день, и worker на нём должен работать как
// везде. Иначе у того, кто заходит в CRM со списка, приложение не встаёт
// вовсе (аудит 20.09.2026).
//
// Внешние страницы (анкета кандидата, приглашение, карточка для заказчика)
// тоже без worker: человеку извне кэш CRM не нужен, а на адресе агентства
// interview.changehr.kz worker из кэша открыл бы вход в CRM мимо сервера.
if (
  !isVideoLivePath(window.location.pathname) &&
  !isPublicLinkPage(window.location.pathname)
) {
  registerSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      let lastCheck = 0;
      const checkUpdate = () => {
        if (document.visibilityState !== "visible" || !navigator.onLine) return;
        // Из обычной страницы в комнату переходят без перезагрузки: worker уже
        // зарегистрирован, и новую сборку в это время не ищем.
        if (isVideoLivePath(window.location.pathname)) return;
        if (Date.now() - lastCheck < 60_000) return;
        lastCheck = Date.now();
        void registration.update().catch(() => {});
      };
      checkUpdate();
      document.addEventListener("visibilitychange", checkUpdate);
      window.addEventListener("online", checkUpdate);
      window.setInterval(checkUpdate, 60 * 60_000);
    },
  });
}

// «Назад» на странице интервью ведёт туда, откуда пришли внутри вкладки:
// заголовок no-referrer прячет источник от браузера, поэтому помним сами.
rememberInAppNavigation(window);

// Migration: clear stale impersonation from localStorage (was moved to sessionStorage)
localStorage.removeItem("impersonate-user-id");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60_000, // 2 min — avoid refetch on every mount
      retry: 1,
    },
  },
});

let isLoggingOut = false;
let isRecovering = false;

const forceLogout = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  console.warn("[Auth] Unauthorized — forcing re-login");
  queryClient.clear();
  signOut(auth).finally(() => {
    isLoggingOut = false;
  });
};

// A single "Please login" response must NOT immediately nuke the session — a
// transient hiccup during a request burst (e.g. issuing an invoice) could
// produce one. Выставление счёта шлёт несколько запросов ОДНОЙ пачкой с одним
// и тем же (возможно протухшим) токеном — они падают все разом, и вторая
// ошибка из той же пачки НЕ доказательство, что сессия мертва. Поэтому:
// параллельные ошибки во время восстановления игнорируем, а решение о
// разлогине принимаем ТОЛЬКО по контрольной проверке auth.me со свежим
// токеном: пользователь вернулся → сессия жива; null → правда разлогинен;
// сервер недоступен (деплой/сеть) → это не повод выкидывать на логин.
const verifySession = async (): Promise<"ok" | "unauthed" | "transient"> => {
  try {
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) return "unauthed";
    const res = await fetch("/api/trpc/auth.me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return "transient";
    const body = await res.json().catch(() => null);
    const me = body?.result?.data?.json ?? null;
    return me ? "ok" : "unauthed";
  } catch {
    return "transient";
  }
};

const handleUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (error.message !== UNAUTHED_ERR_MSG) return;
  if (isLoggingOut || isRecovering) return;

  // No Firebase session at all → genuinely signed out.
  if (!auth.currentUser) {
    forceLogout();
    return;
  }
  isRecovering = true;
  console.warn("[Auth] Unauthorized — verifying session with fresh token");
  verifySession()
    .then(verdict => {
      if (verdict === "unauthed") {
        forceLogout();
      } else {
        // "ok" — токен обновлён, перезапрашиваем данные.
        // "transient" — сервер моргнул (деплой): не разлогиниваем, refetch
        // сам повторится; хуже от invalidate не станет.
        queryClient.invalidateQueries();
      }
    })
    .finally(() => {
      isRecovering = false;
    });
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    handleUnauthorized(event.query.state.error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    handleUnauthorized(event.mutation.state.error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      async headers() {
        const token = await getAuthToken();
        const h: Record<string, string> = {};
        if (token) h["Authorization"] = `Bearer ${token}`;
        const imp = sessionStorage.getItem("impersonate-user-id");
        if (imp) h["X-Impersonate-User-Id"] = imp;
        const branch = localStorage.getItem("branch-id");
        if (branch) h["X-Branch-Id"] = branch;
        // Пропуск в комнату видеоинтервью — заголовком, а не во входе запроса.
        // Опрос комнаты идёт раз в секунду методом GET, и вход у него уезжает
        // в строку адреса: пропуск (ключ к TURN, сигналам связи и приёму
        // записи, живёт два часа) попадал бы в логи прокси и в историю
        // браузера. Сервер читает этот заголовок с 19.09.2026.
        const roomSession = getRoomSessionToken();
        if (roomSession) h[VIDEO_INTERVIEW_SESSION_HEADER] = roomSession;
        return h;
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
